import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import XPProgressHeader from '../../components/ui/XPProgressHeader';
import AIChatbot from '../../components/ui/AIChatbot';
import Icon from '../../components/AppIcon';
import CategorySection from './components/CategorySection';
import QuickAccessPanel from './components/QuickAccessPanel';
import AccessStatsWidget from './components/AccessStatsWidget';
import LockedFeatureBanner from './components/LockedFeatureBanner';

const ToolsAndAccessHub = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  const mockTools = [
    {
      id: 1,
      name: 'Slack',
      description: 'Team communication and collaboration platform for real-time messaging, file sharing, and integrations',
      icon: 'MessageSquare',
      category: 'Communication',
      status: 'connected',
      ssoEnabled: true,
      hasGuide: true,
      tags: ['Messaging', 'Collaboration']
    },
    {
      id: 2,
      name: 'Microsoft Teams',
      description: 'Video conferencing and team collaboration tool with chat, meetings, and file storage capabilities',
      icon: 'Video',
      category: 'Communication',
      status: 'pending',
      ssoEnabled: true,
      hasGuide: true,
      tags: ['Video', 'Meetings']
    },
    {
      id: 3,
      name: 'Gmail',
      description: 'Corporate email service with advanced security, spam filtering, and seamless integration',
      icon: 'Mail',
      category: 'Communication',
      status: 'connected',
      ssoEnabled: true,
      hasGuide: false,
      tags: ['Email', 'Productivity']
    },
    {
      id: 4,
      name: 'GitHub',
      description: 'Version control and code collaboration platform for software development teams',
      icon: 'Github',
      category: 'Development',
      status: 'connected',
      ssoEnabled: true,
      hasGuide: true,
      tags: ['Git', 'Code']
    },
    {
      id: 5,
      name: 'GitLab',
      description: 'Complete DevOps platform with CI/CD pipelines, issue tracking, and code review',
      icon: 'GitBranch',
      category: 'Development',
      status: 'unavailable',
      ssoEnabled: false,
      hasGuide: true,
      tags: ['DevOps', 'CI/CD']
    },
    {
      id: 6,
      name: 'VS Code',
      description: 'Lightweight but powerful source code editor with debugging and Git integration',
      icon: 'Code',
      category: 'Development',
      status: 'connected',
      ssoEnabled: false,
      hasGuide: true,
      tags: ['Editor', 'IDE']
    },
    {
      id: 7,
      name: 'Jira',
      description: 'Project management and issue tracking software for agile teams',
      icon: 'ListTodo',
      category: 'Productivity',
      status: 'connected',
      ssoEnabled: true,
      hasGuide: true,
      tags: ['Project Management', 'Agile']
    },
    {
      id: 8,
      name: 'Notion',
      description: 'All-in-one workspace for notes, docs, wikis, and project management',
      icon: 'FileText',
      category: 'Productivity',
      status: 'pending',
      ssoEnabled: true,
      hasGuide: true,
      tags: ['Documentation', 'Wiki']
    },
    {
      id: 9,
      name: 'Asana',
      description: 'Work management platform to organize, track, and manage team projects',
      icon: 'CheckSquare',
      category: 'Productivity',
      status: 'unavailable',
      ssoEnabled: true,
      hasGuide: false,
      tags: ['Tasks', 'Planning']
    },
    {
      id: 10,
      name: 'Figma',
      description: 'Collaborative interface design tool for creating, prototyping, and sharing designs',
      icon: 'Palette',
      category: 'Design',
      status: 'connected',
      ssoEnabled: true,
      hasGuide: true,
      tags: ['Design', 'Prototyping']
    },
    {
      id: 11,
      name: 'Adobe Creative Cloud',
      description: 'Suite of creative applications including Photoshop, Illustrator, and more',
      icon: 'Sparkles',
      category: 'Design',
      status: 'pending',
      ssoEnabled: false,
      hasGuide: true,
      tags: ['Graphics', 'Creative']
    },
    {
      id: 12,
      name: 'Google Analytics',
      description: 'Web analytics service for tracking and reporting website traffic and user behavior',
      icon: 'BarChart3',
      category: 'Analytics',
      status: 'connected',
      ssoEnabled: true,
      hasGuide: true,
      tags: ['Analytics', 'Tracking']
    }
  ];

  const accessStats = {
    connected: mockTools?.filter(t => t?.status === 'connected')?.length,
    pending: mockTools?.filter(t => t?.status === 'pending')?.length,
    available: mockTools?.length,
    ssoEnabled: mockTools?.filter(t => t?.ssoEnabled)?.length
  };

  const categories = [...new Set(mockTools.map(tool => tool.category))];

  const filteredTools = mockTools?.filter(tool => {
    const matchesSearch = tool?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         tool?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || tool?.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  const groupedTools = categories?.reduce((acc, category) => {
    acc[category] = filteredTools?.filter(tool => tool?.category === category);
    return acc;
  }, {});

  const handleRequestAccess = (tool) => {
    console.log('Requesting access to:', tool?.name);
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action triggered:', actionId);
  };

  const filterOptions = [
    { value: 'all', label: 'All Tools', icon: 'Package' },
    { value: 'connected', label: 'Connected', icon: 'CheckCircle2' },
    { value: 'pending', label: 'Pending', icon: 'Clock' },
    { value: 'unavailable', label: 'Unavailable', icon: 'XCircle' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      <XPProgressHeader currentXP={450} level="Newbie" totalXP={1000} recentGain={0} />
      <AIChatbot />
      <main className={`pt-20 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}`}>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Wrench" size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Tools & Access</h1>
                <p className="text-muted-foreground">Manage your applications and integrations</p>
              </div>
            </div>
          </div>

          <LockedFeatureBanner />

          <AccessStatsWidget stats={accessStats} />

          <QuickAccessPanel onQuickAction={handleQuickAction} />

          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Icon
                  name="Search"
                  size={18}
                  color="var(--color-muted-foreground)"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                />
                <input
                  type="text"
                  placeholder="Search tools by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150"
                />
              </div>

              <div className="flex gap-2 overflow-x-auto">
                {filterOptions?.map((option) => (
                  <button
                    key={option?.value}
                    onClick={() => setSelectedFilter(option?.value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-150 ${
                      selectedFilter === option?.value
                        ? 'bg-primary text-primary-foreground shadow-glow'
                        : 'bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    }`}
                  >
                    <Icon name={option?.icon} size={16} color="currentColor" />
                    {option?.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {Object.entries(groupedTools)?.map(([category, tools]) => (
            tools?.length > 0 && (
              <CategorySection
                key={category}
                category={category}
                tools={tools}
                onRequestAccess={handleRequestAccess}
              />
            )
          ))}

          {filteredTools?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-20 h-20 bg-muted/30 rounded-full flex items-center justify-center mb-4">
                <Icon name="Search" size={40} color="var(--color-muted-foreground)" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">No tools found</h3>
              <p className="text-muted-foreground max-w-md">
                Try adjusting your search or filter criteria to find the tools you're looking for
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ToolsAndAccessHub;